import StatusChip from 'components/@extended/StatusChip';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import { format, parseISO } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useHourlyRates } from 'hooks/useHourlyRates';
import { HourlyRateDetailsCard } from 'pages/pricelists/HourlyRateDetailsCard';
import { employeeRatesOfCaseList } from 'pages/pricelists/modal/employeePricing';
import {
    inForceListOn,
    pricedRoleRows,
    tabLabel,
    undatedPeriodLabel,
} from 'pages/pricelists/modal/matterPricelistEditorUtils';
import { useToday } from 'pages/pricelists/modal/useEmployeePricing';
import { useState } from 'react';
import { PricelistCardEntry, PricingKind } from 'types/pricelists';
import { t } from 'utils/getTranslations';

interface Props {
    lists: PricelistCardEntry[];
    openedDate: string | null;
    currencyTypeCode?: string | null;
    pricingKind?: PricingKind;
    // The lists are the periods of a list the case follows, whose first one covers all work before the next.
    following?: boolean;
}

interface PersonalRowsProps {
    list: PricelistCardEntry;
    currencyTypeCode?: string | null;
}

const PersonalRows = ({ list, currencyTypeCode }: PersonalRowsProps): JSX.Element | null => {
    if (list.personalRates.length === 0) {
        return null;
    }
    return (
        <div className="px-2 pb-2">
            <div className="text-[0.8rem] font-semibold">{t('pricelists.case-editor.individual-rates')}</div>
            <hr className="mt-0.5 mb-0 border-zinc-300/50" />
            {list.personalRates.map((row) => (
                <div
                    key={row.employee.id}
                    className="border-grey-200 flex justify-between border-b pt-1 pb-0.5 text-[0.8rem]"
                >
                    <span>
                        {row.employee.name}
                        {row.jobRole ? ` · ${row.jobRole.internalName ?? row.jobRole.name}` : ''}
                    </span>
                    <span>
                        {currencyTypeCode ? `${currencyTypeCode} ` : ''}
                        {row.hourlyPrice ?? ''}
                    </span>
                </div>
            ))}
        </div>
    );
};

/**
 * The popover behind the case card's info icon: a tab per list in date order, opened on the list in
 * force today, with the personal rows under the role rows. A case with one list shows no tab strip, and a
 * list starting in the future carries the catalogue's Scheduled word. Tabs are keyed by the entry's key,
 * as a list the form staged has no id yet.
 */
export const MatterPricelistsPopover = ({
    lists,
    openedDate,
    currencyTypeCode,
    pricingKind = PricingKind.ROLE,
    following = false,
}: Props): JSX.Element => {
    const dateFormat = useEffectiveDateFormat();
    const today = useToday();
    const [activeKey, setActiveKey] = useState<string | undefined>(undefined);
    const active = lists.find((list) => list.key === activeKey) ?? inForceListOn(lists, today) ?? lists[0];
    const { hourlyRateDetails } = useHourlyRates(active?.rates);
    const formatDate = (date: string) => format(parseISO(date), dateFormat);

    if (!active) {
        return <HourlyRateDetailsCard data={{}} fontSize="0.8rem" />;
    }
    return (
        <div className="flex flex-col">
            {lists.length > 1 ? (
                <LineTabs value={active.key} onValueChange={(key) => setActiveKey(String(key))}>
                    <LineTabsList arrows={{ back: t('back'), next: t('next') }}>
                        {lists.map((list) => (
                            <LineTab key={list.key} value={list.key} size="compact">
                                <span className="flex items-center gap-1">
                                    {following && list.validFrom === null
                                        ? undatedPeriodLabel(lists, formatDate)
                                        : tabLabel(
                                              {
                                                  isBase: list.validFrom === null,
                                                  validFrom: list.validFrom,
                                                  name: list.name,
                                              },
                                              openedDate,
                                              formatDate,
                                              (date) => t('pricelists.case-editor.from-date', { date })
                                          )}
                                    {list.validFrom && list.validFrom > today ? (
                                        <StatusChip type="info" size="small" label={t('pricelists.filter.scheduled')} />
                                    ) : null}
                                </span>
                            </LineTab>
                        ))}
                    </LineTabsList>
                </LineTabs>
            ) : null}
            {pricingKind === PricingKind.EMPLOYEE ? (
                <HourlyRateDetailsCard
                    data={{}}
                    employeeRates={employeeRatesOfCaseList(active.personalRates)}
                    fontSize="0.8rem"
                />
            ) : (
                <>
                    {/* A list priced only per person has no role groups to say are missing. */}
                    {pricedRoleRows(active.rates).length > 0 || active.personalRates.length === 0 ? (
                        <HourlyRateDetailsCard data={hourlyRateDetails} fontSize="0.8rem" />
                    ) : null}
                    <PersonalRows list={active} currencyTypeCode={currencyTypeCode} />
                </>
            )}
        </div>
    );
};
